import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'
import { publicAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'
import {
  Car,
  Building2,
  Users,
  Truck,
  CreditCard,
} from 'lucide-react'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { formatNorwegianDate } from '../utils/dateUtils'

interface SummaryData {
  total_vehicles: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  company_sales: number
  company_sales_percentage: number
  private_sales: number
  private_sales_percentage: number
}

interface BrandRow {
  ranking: number | null
  brand: string
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  mtd_count: number | null
  mtd_market_share: number | null
  ytd_current_count: number | null
  ytd_current_market_share: number | null
}

interface PeriodsInfo {
  current: { label: string; start_date: string; end_date: string }
  previous: { label: string; start_date: string; end_date: string }
  mtd?: { label: string; start_date: string; end_date: string } | null
  ytd_current?: { label: string; start_date: string; end_date: string } | null
}

export default function LastWeekSummary() {
  const { t } = useTranslation('components')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [brands, setBrands] = useState<BrandRow[]>([])
  const [brandsTotalRow, setBrandsTotalRow] = useState<BrandRow | null>(null)
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)
  // null until the API returns the period's own label; shown as "I går" meanwhile.
  const [apiPeriodLabel, setPeriodLabel] = useState<string | null>(null)
  const [personbilCount, setPersonbilCount] = useState(0)
  const [varebilCount, setVarebilCount] = useState(0)
  const mode = 'yesterday' as const
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()

  useEffect(() => {
    fetchLastWeekData()
  }, [vehicleFilter])

  const fetchLastWeekData = async () => {
    try {
      setLoading(true)
      setError(null)

      const varebilParam = getVarebilParam()
      const brandParams: Record<string, unknown> = {
        output_mode: mode,
        limit: 20,
      }
      if (varebilParam !== undefined) {
        brandParams.varebil = varebilParam
      }

      // Fetch brands with last_week mode (this gives us the week dates)
      const brandsRes = await publicAPI.getTotalStatsBrands(brandParams as any)
      const brandsData: BrandRow[] = brandsRes.data.data || []
      const periodsData: PeriodsInfo = brandsRes.data.periods

      // Set the week label from periods
      if (periodsData?.current?.label) {
        setPeriodLabel(periodsData.current.label)
      }
      setPeriods(periodsData)

      // Extract TOTAL row and set brands
      const totalRow = brandsData.find(
        (b) => b.ranking === null && b.brand === 'TOTAL'
      ) || null
      setBrandsTotalRow(totalRow)
      const brandsWithoutTotal = brandsData.filter(
        (b) => b.ranking !== null && b.brand !== 'TOTAL'
      )
      setBrands(brandsWithoutTotal)

      // Now fetch summary for the same week dates
      if (periodsData?.current?.start_date && periodsData?.current?.end_date) {
        const summaryParams: Record<string, unknown> = {
          start_date: periodsData.current.start_date,
          end_date: periodsData.current.end_date,
        }
        if (varebilParam !== undefined) {
          summaryParams.varebil = varebilParam
        }

        const summaryRes = await publicAPI.getSummary(summaryParams as any)
        setSummary(summaryRes.data.data)

        // Only fetch personbil/varebil breakdown in 'alle' mode
        if (vehicleFilter === 'alle') {
          try {
            const [personbilRes, varebilRes] = await Promise.all([
              publicAPI.getSummary({
                start_date: periodsData.current.start_date,
                end_date: periodsData.current.end_date,
                varebil: false,
              }),
              publicAPI.getSummary({
                start_date: periodsData.current.start_date,
                end_date: periodsData.current.end_date,
                varebil: true,
              }),
            ])
            setPersonbilCount(personbilRes.data.data?.total_vehicles || 0)
            setVarebilCount(varebilRes.data.data?.total_vehicles || 0)
          } catch {
            setPersonbilCount(0)
            setVarebilCount(0)
          }
        } else {
          setPersonbilCount(0)
          setVarebilCount(0)
        }
      }
    } catch (err: any) {
      setError(
        localizeApiDetail(err.response?.data?.detail) || err.message || t('lastWeekSummary.loadError')
      )
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!summary) return null

  const periodLabel = apiPeriodLabel ?? t('lastWeekSummary.yesterday')
  const totalVehicles = summary.total_vehicles || 0
  const personbilShare =
    totalVehicles > 0 ? ((personbilCount / totalVehicles) * 100) : 0
  const varebilShare =
    totalVehicles > 0 ? ((varebilCount / totalVehicles) * 100) : 0

  const fmtPct = (v: number | string) =>
    Number(v).toLocaleString('nb-NO', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) + ' %'
  const fmtPctBare = (v: number | string) =>
    Number(v).toLocaleString('nb-NO', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
  const fmtNum = (v: number) => v.toLocaleString('nb-NO')

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {periodLabel}
          </h2>
          {periods?.current && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {formatNorwegianDate(periods.current.start_date)} – {formatNorwegianDate(periods.current.end_date)}
            </p>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label={t('lastWeekSummary.newRegistrations')}
          value={fmtNum(totalVehicles)}
          icon={<Car className="w-4 h-4" />}
          color="blue"
        />
        <KpiCard
          label={t('lastWeekSummary.leasingShare')}
          value={fmtPct(summary.leasing_percentage)}
          icon={<CreditCard className="w-4 h-4" />}
          color="purple"
        />
        <KpiCard
          label={t('lastWeekSummary.businessShare')}
          value={fmtPct(summary.company_sales_percentage)}
          icon={<Building2 className="w-4 h-4" />}
          color="orange"
        />
        <KpiCard
          label={t('lastWeekSummary.privateShare')}
          value={fmtPct(summary.private_sales_percentage)}
          icon={<Users className="w-4 h-4" />}
          color="green"
        />
        {vehicleFilter === 'alle' && (
          <>
            <KpiCard
              label={t('lastWeekSummary.personbil')}
              value={`${fmtNum(personbilCount)} (${fmtPct(personbilShare)})`}
              icon={<Car className="w-4 h-4" />}
              color="blue"
            />
            <KpiCard
              label={t('lastWeekSummary.varebil')}
              value={`${fmtNum(varebilCount)} (${fmtPct(varebilShare)})`}
              icon={<Truck className="w-4 h-4" />}
              color="slate"
            />
          </>
        )}
      </div>

      {/* Top 20 brands table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('lastWeekSummary.top20', { period: periodLabel })}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-card border-b-2 border-gray-200 dark:border-dark-border">
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-center">#</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-left">{t('lastWeekSummary.brand')}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right">{periodLabel}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right border-r-2 border-gray-300 dark:border-gray-600">{t('lastWeekSummary.sharePct')}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right">{periods?.mtd?.label ?? t('lastWeekSummary.monthToDate')}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right border-r-2 border-gray-300 dark:border-gray-600">{t('lastWeekSummary.sharePct')}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right">{t('lastWeekSummary.yearToDate')}</th>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right">{t('lastWeekSummary.sharePct')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Pinned TOTAL row */}
              {brandsTotalRow && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                  <td className="px-3 py-1 text-xs text-center">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                  </td>
                  <td className="px-3 py-1 text-xs font-bold text-primary-700 dark:text-primary-300">
                    TOTAL
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300">
                    {fmtNum(summary?.total_vehicles ?? brandsTotalRow.current_count)}
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300 border-r-2 border-gray-300 dark:border-gray-600">
                    {fmtPctBare(brandsTotalRow.current_market_share)}
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300">
                    {brandsTotalRow.mtd_count != null ? fmtNum(brandsTotalRow.mtd_count) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300 border-r-2 border-gray-300 dark:border-gray-600">
                    {brandsTotalRow.mtd_market_share != null ? fmtPctBare(brandsTotalRow.mtd_market_share) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300">
                    {brandsTotalRow.ytd_current_count != null ? fmtNum(brandsTotalRow.ytd_current_count) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right font-bold text-primary-700 dark:text-primary-300">
                    {brandsTotalRow.ytd_current_market_share != null ? fmtPctBare(brandsTotalRow.ytd_current_market_share) : '–'}
                  </td>
                </tr>
              )}
              {brands.map((row, idx) => (
                <tr
                  key={row.brand}
                  className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${
                    idx % 2 === 0 ? 'bg-gray-50 dark:bg-white/6' : ''
                  }`}
                >
                  <td className="px-3 py-1 text-xs text-center text-gray-500 dark:text-gray-400">
                    {row.ranking}
                  </td>
                  <td className="px-3 py-1 text-xs font-bold text-gray-900 dark:text-gray-100">
                    {row.brand}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-900 dark:text-gray-100">
                    {fmtNum(row.current_count)}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-600 dark:text-gray-300 border-r-2 border-gray-300 dark:border-gray-600">
                    {fmtPctBare(row.current_market_share)}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-900 dark:text-gray-100">
                    {row.mtd_count != null ? fmtNum(row.mtd_count) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-600 dark:text-gray-300 border-r-2 border-gray-300 dark:border-gray-600">
                    {row.mtd_market_share != null ? fmtPctBare(row.mtd_market_share) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-900 dark:text-gray-100">
                    {row.ytd_current_count != null ? fmtNum(row.ytd_current_count) : '–'}
                  </td>
                  <td className="px-3 py-1 text-xs text-right text-gray-600 dark:text-gray-300">
                    {row.ytd_current_market_share != null ? fmtPctBare(row.ytd_current_market_share) : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function KpiCard({
  label,
  value,
  icon,
  color,
}: {
  label: string
  value: string
  icon: React.ReactNode
  color: string
}) {
  const bgColors: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple:
      'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    orange:
      'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
    slate:
      'bg-slate-50 dark:bg-slate-900/20 text-slate-600 dark:text-slate-400',
  }

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-md ${bgColors[color] || bgColors.blue}`}>
          {icon}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
          {label}
        </span>
      </div>
      <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
        {value}
      </p>
    </div>
  )
}
